import type { ChangeEvent } from 'react';

import {
  CopyToClipboard,
  Heading,
  InputDescription,
  SaveButton,
  TextInput,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetLsproSecuritySettingsQuery,
  useUpdateLiveStreamProSecuritySettingsMutation,
} from '../../../../generated/graphql-manager';
import { useCurrentUser } from '../../../hooks/current-user';

import managerStyles from '../../../manager.module.css';

export function SecurityPage() {
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();

  const [sharedSecretVmpro, setSharedSecretVmpro] = useState('');
  const [sharedSecretVmproError, setSharedSecretVmproError] = useState('');

  const { data } = useGetLsproSecuritySettingsQuery({
    variables: { lsproId },
    onCompleted: (data) => {
      setSharedSecretVmpro(data?.lsproSettings?.securitySettings?.sharedSecretVmpro || '');
    },
  });

  const securityToken = data?.lsproSettings?.securitySettings?.tokenGenerationSecret;

  const [updateLiveStreamProSecuritySettings, { loading: updateLsproSecuritySettingsLoading }] =
    useUpdateLiveStreamProSecuritySettingsMutation();

  const validateSharedSecret = (value: string): string => {
    if (!value) return '';

    if (value.length !== 32) return t('manager.channelSettings.security.secretVmproValidation.length');

    const hexPattern = /^[0-9a-fA-F]+$/;
    if (!hexPattern.test(value)) return t('manager.channelSettings.security.secretVmproValidation.format');

    return '';
  };

  const handleSaveVmproSecret = async () => {
    const error = validateSharedSecret(sharedSecretVmpro);
    setSharedSecretVmproError(error);

    if (error) return;

    await updateLiveStreamProSecuritySettings({
      variables: {
        input: {
          lsproId,
          sharedSecretVmpro: sharedSecretVmpro,
        },
      },
      optimisticResponse: {
        updateLiveStreamProSecuritySettings: {
          __typename: 'UpdateLiveStreamProSecuritySettingsSuccess',
          sharedSecretVmpro: sharedSecretVmpro,
        },
      },
      onCompleted: (dataCompleted) => {
        if (
          dataCompleted.updateLiveStreamProSecuritySettings.__typename === 'UpdateLiveStreamProSecuritySettingsFailure'
        ) {
          setSharedSecretVmpro(data?.lsproSettings?.securitySettings.sharedSecretVmpro || '');
        }
      },
    });
  };

  const handleSharedSecretChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSharedSecretVmpro(event.target.value);
    setSharedSecretVmproError('');
  };

  return (
    <main className={managerStyles.main} data-testid="security-page">
      <Heading className={managerStyles.grayText} data-testid="security-heading">
        {t('manager.channelSettings.security.heading')}
      </Heading>

      <InputDescription inputId="security-token" label={t('manager.channelSettings.security.tokenLabel')}>
        <TextInput readOnly value={securityToken || ''} data-testid="security-token">
          <CopyToClipboard value={securityToken || ''} notificationPosition="left" />
        </TextInput>
      </InputDescription>

      <InputDescription
        inputId="shared-secret-vmpro"
        label={t('manager.channelSettings.security.secretVmproLabel')}
        helpMessage={t('manager.channelSettings.security.secretVmproHelpMessage')}
        errorMessage={sharedSecretVmproError}
      >
        <TextInput value={sharedSecretVmpro} data-testid="shared-secret-vmpro" onChange={handleSharedSecretChange}>
          <CopyToClipboard value={sharedSecretVmpro} notificationPosition="left" />
        </TextInput>
      </InputDescription>

      <div className={managerStyles.buttons}>
        <SaveButton
          data-testid="security-submit-button"
          onClick={handleSaveVmproSecret}
          saving={updateLsproSecuritySettingsLoading}
          disabled={updateLsproSecuritySettingsLoading}
        />
      </div>
    </main>
  );
}
